/**
 * Tiny client for the game server's JSON API.
 *
 * Every call here is **optional to the game**. The match is entirely local — the server
 * only collects a leaderboard — so nothing in this module may ever throw into the game
 * loop or block a screen transition. Failures resolve to `null` and are logged once at
 * debug level.
 */

import type { PlayerId } from '../game/types';

/** Shape the server accepts on POST /api/scores. Keep in sync with server/index.mjs. */
export interface MatchReport {
  seed: number;
  rounds: number;
  durationMs: number;
  winner: PlayerId | null;
  players: {
    name: string;
    seat: PlayerId;
    vp: number;
    kind: 'human' | 'ai';
  }[];
}

export interface ScoreRow extends MatchReport {
  id: number;
  ts: string;
}

/** Envelope the server wraps score listings in. */
interface ScoresResponse {
  ok: boolean;
  count: number;
  total: number;
  scores: ScoreRow[];
}

/** Mirrors GET /api/stats exactly — see server/index.mjs. */
export interface ServerStats {
  ok: boolean;
  matchesPlayed: number;
  averageDurationMs: number;
  /** Keyed by seat index as a string, because it arrives as a JSON object. */
  winsBySeat: Record<string, number>;
  draws: number;
  humanWins: number;
  aiWins: number;
  averageWinningVp: number;
  lastMatchAt: string | null;
}

/** Abort any request that has not answered in this long. */
const TIMEOUT_MS = 4000;

let warned = false;

/** Log the first failure only — a self-hosted game with no server should stay quiet. */
function warnOnce(context: string, error: unknown): void {
  if (warned) return;
  warned = true;
  // eslint-disable-next-line no-console -- one-time diagnostic, intentionally kept
  console.debug(`[nova-charter] score API unavailable (${context}):`, error);
}

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T | null> {
  // `AbortSignal.timeout` is not in older Safari, so fall back to a manual controller.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(path, {
      ...init,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
    if (!response.ok) {
      warnOnce(`${path} → ${response.status}`, await safeText(response));
      return null;
    }
    return (await response.json()) as T;
  } catch (error) {
    warnOnce(path, error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function safeText(response: Response): Promise<string> {
  try {
    return (await response.text()).slice(0, 200);
  } catch {
    return '<unreadable>';
  }
}

/**
 * Submit a finished match. Fire-and-forget: callers should not await this.
 *
 * The server validates strictly and rejects unknown keys, so `MatchReport` must stay in
 * step with its schema — in particular it wants all four seats, not just the humans.
 */
export function postScore(
  report: MatchReport,
): Promise<{ ok: boolean; id: number; kept: boolean } | null> {
  return request<{ ok: boolean; id: number; kept: boolean }>('/api/scores', {
    method: 'POST',
    body: JSON.stringify(report),
  });
}

/** Recent match results, newest first. `null` when the server is unreachable. */
export async function fetchScores(limit = 20): Promise<ScoreRow[] | null> {
  const clamped = Math.max(1, Math.min(100, Math.floor(limit)));
  const body = await request<ScoresResponse>(`/api/scores?limit=${clamped}`);
  // Unwrap the envelope so callers get the plain list they asked for.
  return body?.scores ?? null;
}

export function fetchStats(): Promise<ServerStats | null> {
  return request<ServerStats>('/api/stats');
}

/** Health probe, used by the deploy script's smoke test and by a debug overlay. */
export function fetchHealth(): Promise<{ ok: boolean; version: string; commit: string } | null> {
  return request<{ ok: boolean; version: string; commit: string }>('/api/health');
}
